/* ======================================================================
   CART DRAWER — GLASS
   Comportamiento del carrito: rotación de mensajes de envío, plegado del
   cross-sell, barra del carrusel y selector de talla de la compra rápida y
   recomendaciones del cross-sell.

   Todo va por delegación en document y es idempotente: la sección del
   carrito se vuelve a renderizar por Ajax en cada cambio del carrito y
   este script puede reinyectarse.
   ====================================================================== */

(() => {
  if (window.__ccCartGlass) return;
  window.__ccCartGlass = true;

  const ROTATION_FALLBACK = 4000;

  /* --------------------------------------------------------------------
     Mensajes rotativos de la barra de envío
     -------------------------------------------------------------------- */

  const rotateMessages = () => {
    document.querySelectorAll('.ccg-progress__stack').forEach((stack) => {
      const messages = Array.from(stack.querySelectorAll('[data-ccg-msg]')).filter(
        (message) => message.textContent.trim() !== ''
      );

      if (messages.length < 2) {
        if (messages.length === 1) messages[0].dataset.active = 'true';
        return;
      }

      const currentIndex = messages.findIndex((message) => message.dataset.active === 'true');
      const nextIndex = (currentIndex + 1) % messages.length;

      messages.forEach((message, index) => {
        message.dataset.active = index === nextIndex ? 'true' : 'false';
      });
    });
  };

  let rotationDelay = ROTATION_FALLBACK;
  const firstStack = document.querySelector('.ccg-progress__stack[data-rotation]');
  if (firstStack) rotationDelay = parseInt(firstStack.dataset.rotation, 10) || ROTATION_FALLBACK;

  setInterval(rotateMessages, rotationDelay);

  /* --------------------------------------------------------------------
     Barra de posición del carrusel de cross-sell
     -------------------------------------------------------------------- */

  const updateScrollbar = (scroller) => {
    const upsell = scroller.closest('.ccg-upsell');
    const thumb = upsell && upsell.querySelector('.ccg-upsell__scrollbar span');
    if (!thumb) return;

    const scrollable = scroller.scrollWidth - scroller.clientWidth;

    if (scrollable <= 1) {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
      return;
    }

    const ratio = scroller.clientWidth / scroller.scrollWidth;
    const progress = scroller.scrollLeft / scrollable;

    thumb.style.width = `${Math.max(12, ratio * 100)}%`;
    thumb.style.transform = `translateX(${(progress * (1 - ratio) * 100) / Math.max(ratio, 0.0001)}%)`;
  };

  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target;
      if (scroller instanceof Element && scroller.classList.contains('ccg-upsell__scroller')) {
        updateScrollbar(scroller);
      }
    },
    true
  );

  const refreshScrollbars = () => {
    document.querySelectorAll('.ccg-upsell__scroller').forEach(updateScrollbar);
  };

  document.addEventListener('cart:change', () => requestAnimationFrame(refreshScrollbars));
  document.addEventListener('cart:refresh', () => requestAnimationFrame(refreshScrollbars));
  window.addEventListener('resize', refreshScrollbars);
  requestAnimationFrame(refreshScrollbars);

  /* --------------------------------------------------------------------
     Interacciones (delegadas)
     -------------------------------------------------------------------- */

  const closeAllVariantPickers = (except) => {
    document.querySelectorAll('.ccg-card[data-variants-open="true"]').forEach((card) => {
      if (card !== except) card.dataset.variantsOpen = 'false';
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Plegar / desplegar el cross-sell
    const toggle = target.closest('.ccg-upsell__toggle');
    if (toggle) {
      const upsell = toggle.closest('.ccg-upsell');
      const collapsed = upsell.dataset.collapsed === 'true';
      upsell.dataset.collapsed = collapsed ? 'false' : 'true';
      toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
      if (!collapsed) closeAllVariantPickers();
      else requestAnimationFrame(refreshScrollbars);
      return;
    }

    // Abrir el selector de talla de una tarjeta con varias variantes
    const addButton = target.closest('.ccg-card__add');
    if (addButton && addButton.dataset.hasVariants === 'true') {
      event.preventDefault();
      const card = addButton.closest('.ccg-card');
      const isOpen = card.dataset.variantsOpen === 'true';
      closeAllVariantPickers(card);
      card.dataset.variantsOpen = isOpen ? 'false' : 'true';
      return;
    }

    // Elegir talla: fija la variante y deja que el formulario del tema envíe
    const sizeButton = target.closest('.ccg-card__size');
    if (sizeButton) {
      const form = sizeButton.closest('form');
      const idInput = form && form.querySelector('input[name="id"]');
      if (idInput) idInput.value = sizeButton.dataset.variantId;
      const card = sizeButton.closest('.ccg-card');
      if (card) card.dataset.variantsOpen = 'false';
      return;
    }

    // Clic fuera: cerrar selectores abiertos
    if (!target.closest('.ccg-card__variants')) closeAllVariantPickers();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllVariantPickers();
  });

  /* --------------------------------------------------------------------
     Recomendaciones del cross-sell
     La sección del carrito trae ya los productos fijos y los de la
     colección. Aquí se piden a Shopify las recomendaciones de los
     productos del carrito (primero las complementarias de Search &
     Discovery; si no hay, las automáticas), se ponen delante y se recorta
     al máximo configurado. De las automáticas se quitan las del mismo tipo
     que lo que hay en el carrito: suelen ser la misma prenda en otro color,
     y eso no es cross-sell. Cada vez que el carrito se vuelve a pintar,
     la tarjeta nueva llega sin data-reco-state y se repite.
     -------------------------------------------------------------------- */

  const RECO_SECTION = 'cart-recommendations';
  const recoCache = new Map();

  const fetchRecommendations = (productId, intent, limit) => {
    const key = `${productId}:${intent}`;
    if (recoCache.has(key)) return recoCache.get(key);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${RECO_SECTION}`;

    const request = fetch(url)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html;
        return Array.from(template.content.querySelectorAll('.ccg-card'));
      })
      .catch(() => []);

    recoCache.set(key, request);
    return request;
  };

  const recommendationsFor = async (productId, limit, cartTypes) => {
    const complementary = await fetchRecommendations(productId, 'complementary', limit);
    if (complementary.length > 0) return complementary;
    const related = await fetchRecommendations(productId, 'related', limit);
    return related.filter((card) => !cartTypes.has(card.dataset.productType || ''));
  };

  const fetchCartTypes = () =>
    fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => new Set(cart.items.map((item) => item.product_type).filter(Boolean)))
      .catch(() => new Set());

  const hydrateUpsell = async (upsell) => {
    upsell.dataset.recoState = 'loading';

    const scroller = upsell.querySelector('.ccg-upsell__scroller');
    const limit = parseInt(upsell.dataset.limit, 10) || 8;
    const productIds = (upsell.dataset.recoProducts || '').split(',').filter(Boolean);
    const inCart = new Set((upsell.dataset.cartProducts || '').split(',').filter(Boolean));

    const cartTypes = productIds.length > 0 ? await fetchCartTypes() : new Set();
    const groups = await Promise.all(productIds.map((id) => recommendationsFor(id, limit, cartTypes)));

    // La tarjeta pudo sustituirse mientras llegaba la respuesta
    if (!upsell.isConnected || !scroller) return;

    // Se intercalan las recomendaciones de cada producto del carrito
    const shown = new Set(Array.from(scroller.querySelectorAll('.ccg-card')).map((card) => card.dataset.productId));
    const picked = [];
    const longest = Math.max(0, ...groups.map((group) => group.length));

    for (let index = 0; index < longest; index++) {
      groups.forEach((group) => {
        const card = group[index];
        if (!card) return;
        const id = card.dataset.productId;
        if (inCart.has(id) || shown.has(id)) return;
        shown.add(id);
        picked.push(card);
      });
    }

    const fragment = document.createDocumentFragment();
    picked.forEach((card) => fragment.appendChild(document.importNode(card, true)));
    scroller.prepend(fragment);

    const cards = scroller.querySelectorAll('.ccg-card');
    Array.from(cards).slice(limit).forEach((card) => card.remove());

    upsell.hidden = scroller.querySelector('.ccg-card') === null;
    upsell.dataset.recoState = 'done';
    requestAnimationFrame(refreshScrollbars);
  };

  const hydrateAll = () => {
    document.querySelectorAll('.ccg-upsell[data-reco-products]:not([data-reco-state])').forEach(hydrateUpsell);
  };

  new MutationObserver(hydrateAll).observe(document.documentElement, { childList: true, subtree: true });
  hydrateAll();
})();
